import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If we're mid-auth (Google OAuth callback OR magic-link verify),
    // skip the /me check — the dedicated callback pages will exchange the
    // token, set the cookie, and call setUser directly. Racing checkAuth
    // against those flows causes a 401 to clobber the just-set user.
    if (typeof window !== "undefined") {
      const midOAuth = window.location.hash?.includes("session_id=");
      const midMagic = window.location.pathname === "/auth/magic";
      if (midOAuth || midMagic) {
        setLoading(false);
        return;
      }
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.debug("logout failed (ignored):", e); }
    setUser(null);
  }, []);

  const logoutEverywhere = useCallback(async () => {
    try { await api.post("/auth/logout-everywhere"); }
    catch (e) { console.debug("logout-everywhere failed (ignored):", e); }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, setUser, loading, checkAuth, logout, logoutEverywhere }),
    [user, loading, checkAuth, logout, logoutEverywhere],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
